import * as React from "react";
import {
  Backdrop,
  Button,
  CircularProgress,
  Grid,
  InputLabel,
  Modal as MuiModal,
  TextField,
  MenuItem,
  Typography,
} from "@mui/material";
import { ClearIcon } from "@mui/x-date-pickers/icons";
import { AuthenticationContext } from "../Context";
import { getHeaders } from "../utils/HeaderHelper";
import { 
  MAX_SUBJECT_LENGTH, 
  getSubjectError,
  MAX_DESCRIPTION_LENGTH,
  getDescriptionError 
} from "../utils/DemographicValidationHelper";
import { useFlash } from "../FlashContext";

interface Props {
  showNeedHelpModal: boolean;
  closeNeedHelpModal: () => void;
}

const NeedHelpModal: React.FC<Props> = ({
  showNeedHelpModal,
  closeNeedHelpModal,
}) => {
  const authenticationSetting = React.useContext(AuthenticationContext);
  const { showToast } = useFlash();

  const [category, setCategory] = React.useState("");
  const [subject, setSubject] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const categoryOptions = [
    { value: "Technical_issue", label: "Technical Issue" },
    { value: "question", label: "Question" },
    { value: "feature_request", label: "Feature Request" },
    { value: "other", label: "Other" },
  ];

  const resetHelpForm = () => {
    setCategory("");
    setSubject("");
    setDescription("");
  };

  const handleSubmit = async () => {
    if (!category) {
      showToast({
        title: "Invalid Category",
        text: "Please select a category",
        type: "error",
      });
    } else if (!subject) {
      showToast({
        title: "Invalid Subject",
        text: "Please enter subject",
        type: "error",
      });
    } else {
      setLoading(true);
      const help_form = {
        category: category,
        subject: subject,
        description: description,
      };

      try {
        const response = await fetch("/data_fetching/send_help_request", {
          method: "POST",
          headers: getHeaders(authenticationSetting.csrfToken),
          body: JSON.stringify({ help_form }),
        });
        const result = await response.json();
        if (result.success) {
          showToast({
            title: "Request Sent",
            text: "Help request sent successfully",
            type: "success",
          });
          resetHelpForm();
          closeNeedHelpModal();
        } else {
          showToast({
            title: "Error",
            text: "Something went wrong, try again later",
            type: "error",
          });
        }
      } catch (error) {
        showToast({
          title: "Request Failed",
          text:
            error.message ??
            "Failed to send help request, please try again later",
          type: "error",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <MuiModal
      open={showNeedHelpModal}
      className="modal-primary"
      onClose={closeNeedHelpModal}
    >
      <div className="paper" style={{ width: 650 }}>
        <div className="paperInner">
          <Grid container>
            <Grid item xs={12}>
              <div className="main-header">
                <span>Need Help?</span>
                <span id="dismiss-button" onClick={closeNeedHelpModal}>
                  <ClearIcon sx={{ width: 30, height: 30 }} />
                </span>
              </div>
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                paddingLeft: "24px",
                paddingRight: "24px",
                marginBottom: "20px",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  lineHeight: "22px",
                  color: "#1E1E1E",
                }}
              >
                If you’re stuck, confused, or just need a hand, send us a quick
                message. Our admin team will review your request and get back to
                you as soon as possible!
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                paddingLeft: "24px",
                paddingRight: "24px",
                display: "flex",
                flexDirection: "column",
                rowGap: "20px",
              }}
            >
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="category" className="field-label">
                    Select a Category*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="category"
                    value={category}
                    required
                    size="small"
                    variant="outlined"
                    className="the-field"
                    select
                    onChange={(event) => {
                      setCategory(event.target.value);
                    }}
                    sx={{
                      width: "100% !important",
                      backgroundColor: "#efe9e7 !important",
                      borderRadius: "5px",
                      "& fieldset": {
                        border: "none",
                        outline: "none",
                      },
                    }}
                  >
                    {categoryOptions?.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="subject" className="field-label">
                    Subject*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="subject"
                    size="small"
                    value={subject}
                    className="the-field"
                    required
                    variant="outlined"
                    placeholder="Subject"
                    onChange={(event) => {
                      const value = event.target.value;
                      const trimmedValue = value.replace(/^\s+/, '');
                      if (trimmedValue.length <= MAX_SUBJECT_LENGTH) {
                        setSubject(trimmedValue);
                      }
                    }}
                    inputProps={{
                      maxLength: MAX_SUBJECT_LENGTH,
                    }}
                    error={Boolean(getSubjectError(subject, true))}
                    helperText={getSubjectError(subject, true)}
                    sx={{
                      width: "100% !important",
                      backgroundColor: "#efe9e7 !important",
                      borderRadius: "5px",
                      "& fieldset": {
                        border: "none",
                        outline: "none",
                      },
                    }}
                  />
                </Grid>
              </Grid>

              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="description" className="field-label">
                    Description
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="description"
                    size="small"
                    value={description}
                    className="the-field"
                    multiline
                    rows={4}
                    variant="outlined"
                    placeholder="Tell us what’s happening."
                    onChange={(event) => {
                      const value = event.target.value;
                      const trimmedValue = value.replace(/^\s+/, '');
                      if (trimmedValue.length <= MAX_DESCRIPTION_LENGTH) {
                        setDescription(trimmedValue);
                      }
                    }}
                    inputProps={{
                      maxLength: MAX_DESCRIPTION_LENGTH, // Browser-level hard stop
                    }}
                    error={Boolean(getDescriptionError(description, true))}
                    helperText={getDescriptionError(description, true)}
                    sx={{
                      width: "100% !important",
                      backgroundColor: "#efe9e7 !important",
                      borderRadius: "5px",
                      "& fieldset": {
                        border: "none",
                        outline: "none",
                      },
                      "& .MuiOutlinedInput-root": {
                        padding: "0px !important",
                      },
                      "& .MuiInputBase-inputMultiline": {
                        padding: "10.5px !important",
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            className="modal-footer"
          >
            <Grid item xs={12} className="confirm-btn-container">
              <div onClick={closeNeedHelpModal} className="cancel-link">
                Cancel
              </div>
              <Button
                onClick={handleSubmit}
                className="confirm-btn"
                style={{ width: 156 }}
              >
                Send
              </Button>
            </Grid>
          </Grid>
        </div>
        <Backdrop open={loading} style={{ zIndex: 9999, color: "#fff" }}>
          <CircularProgress />
        </Backdrop>
      </div>
    </MuiModal>
  );
};

export default NeedHelpModal;
